// Spec §69: report editor. The web client is a thin client — it cannot
// take photos from a camera (the browser APIs are not on par with the
// native image_picker). It does support reading + editing + saving any
// report that already exists in the cloud, plus the "Submit" action.
//
// Phase 1.5: autosave debounce dropped from 5 s to 1.2 s so the user
// sees a fresh "Saved" badge soon after they stop typing. A `pagehide`
// / `visibilitychange` flush attempts to persist the last keystroke
// if the user navigates away, refreshes, or backgrounds the tab within
// the debounce window. Browsers do not guarantee that requests fired
// from these events will complete before the page unloads — best-effort
// only.
//
// Phase 2.1: Web Speech Recognition via the browser's SpeechRecognition
// API. No audio is saved or uploaded — only recognized text is inserted
// into the editor body. Graceful fallback for unsupported browsers.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { photosApi, reportsApi } from '@/api/reportsApi';
import { ApiException } from '@/api/errors';
import {
  useSpeechRecognition,
  SPEECH_LANGUAGE_LABEL,
  SPEECH_PRIVACY_NOTICE,
  type SpeechLanguage,
} from '@/hooks/useSpeechRecognition';
import { PhotoStrip } from './PhotoStrip';
import { usePhotoUploadController } from './photoUpload/usePhotoUploadController';
import { PhotoViewer } from './PhotoViewer';
import type { ReportPhotoJson } from '@/api/types';

const SAVE_DEBOUNCE_MS = 1200;
/** Storage key for the one-time Web Speech privacy acknowledgement. */
const VOICE_ACK_STORAGE_KEY = 'fieldreport:web-voice-ack';

export function ReportEditorScreen() {
  const { id: routeId } = useParams<{ id: string }>();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [version, setVersion] = useState<number | null>(null);
  const [reportId, setReportId] = useState<string | null>(routeId ?? null);
  const [busy, setBusy] = useState<boolean>(!!routeId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);

  // PHASE 7A: configurable voice language + one-time privacy ack.
  const [voiceLanguage, setVoiceLanguage] = useState<SpeechLanguage>(() => {
    if (typeof window === 'undefined') return 'en-US';
    try {
      const stored = window.localStorage.getItem('fieldreport:web-voice-lang');
      if (stored === 'en-US' || stored === 'zh-CN') return stored;
    } catch {
      // ignore — localStorage may be disabled
    }
    const nav = (navigator.language ?? 'en-US').toLowerCase();
    return nav.startsWith('zh') ? 'zh-CN' : 'en-US';
  });
  const [voiceAckShown, setVoiceAckShown] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(VOICE_ACK_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const contentTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Photo viewer state: the photo currently shown in the full-res viewer.
  const [viewerPhoto, setViewerPhoto] = useState<ReportPhotoJson | null>(null);
  // Error shown above the photo strip when a delete fails.
  const [deletePhotoError, setDeletePhotoError] = useState<string | null>(null);
  // Guard against concurrent delete calls for the same photo.
  const deleteInFlightRef = useRef<Set<string>>(new Set());

  // PHASE 9: in-memory list of committed photos for this report.
  // Populated after the editor's reportId becomes available and
  // refreshed after each successful finalize. The list is
  // intentionally local (no global store) so the editor stays
  // self-contained and unmounts cleanly when the user navigates.
  const [committedPhotos, setCommittedPhotos] = useState<import('@/api/types').ReportPhotoJson[]>(
    [],
  );

  // Queue of server photos confirmed via drainTask finalize callbacks.
  // Using a queue (array) rather than a single value is critical:
  // drainTask is called without `await` so two tasks can finish in
  // the same microtask. Both `onServerPhoto` callbacks fire
  // synchronously; a single-value ref would have the second write
  // overwrite the first, and the useEffect would lose the first
  // photo. The queue accumulates all photos that arrived since the
  // last effect drain, and the effect processes the full batch.
  const serverPhotoQueueRef = useRef<import('@/api/types').ReportPhotoJson[]>([]);

  // PHASE 9: serial upload queue. See
  // `usePhotoUploadController.ts` for the idempotency + retry
  // contract. The hook's `inFlightCount` is the source of truth for
  // the Submit gate below.
  //
  // `onServerPhoto` is passed so the controller can notify us of each
  // confirmed server photo immediately after finalize — before the
  // uploaded task is removed from the queue — avoiding the double-
  // render window where both CommittedTile and TaskTile appear for
  // the same photo.
  const upload = usePhotoUploadController({
    reportId,
    getToken,
    onServerPhoto: (serverPhoto) => {
      serverPhotoQueueRef.current.push(serverPhoto);
    },
  });

  // Persist the user's selected voice language so it survives reloads.
  useEffect(() => {
    try {
      window.localStorage.setItem('fieldreport:web-voice-lang', voiceLanguage);
    } catch {
      // ignore
    }
  }, [voiceLanguage]);

  // Content ref so the speech handler can append text.
  const contentRef = useRef(content);
  contentRef.current = content;

  // Web Speech Recognition. The onResult callback appends recognized
  // text to the current body and marks the document dirty so the
  // next autosave picks it up. No audio is saved or transmitted by
  // FieldReport AI (the browser's Web Speech API may however send the
  // captured audio to its own speech service — see SPEECH_PRIVACY_NOTICE).
  const {
    state: voiceState,
    unsupported,
    unsupportedReason,
    start: startVoice,
    stop: stopVoice,
  } = useSpeechRecognition({
    language: voiceLanguage,
    onResult: useCallback((transcript: string) => {
      const current = contentRef.current;
      // Append with a space separator if the body already has content.
      const separator = current.trim() ? ' ' : '';
      setContent((prev) => prev + separator + transcript);
      dirtyRef.current = true;
      setVoiceError(null);
    }, []),
    onError: useCallback((msg: string) => {
      setVoiceError(msg);
    }, []),
  });

  // Refs track the latest values without re-running the autosave
  // effect. The flush handler reads from these refs so it sees the
  // freshest keystroke even if a re-render hasn't flushed yet.
  const titleRef = useRef(title);
  const versionRef = useRef<number | null>(version);
  const reportIdRef = useRef<string | null>(reportId);
  const dirtyRef = useRef(false);
  const inflightRef = useRef(false);
  titleRef.current = title;
  contentRef.current = content;
  versionRef.current = version;
  reportIdRef.current = reportId;

  // Bootstrap: if no id, create a new draft.
  useEffect(() => {
    if (reportId) return;
    let cancelled = false;
    async function create() {
      setBusy(true);
      try {
        const created = await reportsApi.create(getToken, {
          client_report_id: crypto.randomUUID(),
          title: '',
          content: '',
        });
        if (cancelled) return;
        setReportId(created.id);
        setVersion(created.version);
        navigate(`/reports/${created.id}/edit`, { replace: true });
      } catch (e) {
        if (cancelled) return;
        const err = e instanceof ApiException ? e : null;
        setError(err?.message ?? 'Could not create a draft.');
      } finally {
        if (!cancelled) setBusy(false);
      }
    }
    void create();
    return () => {
      cancelled = true;
    };
  }, [reportId, getToken, navigate]);

  // Load existing draft.
  useEffect(() => {
    if (!reportId) return;
    let cancelled = false;
    async function load() {
      setBusy(true);
      try {
        const row = await reportsApi.get(getToken, reportId!);
        if (cancelled) return;
        // Mobile lifecycle rule (commit 6343a80): the Editor is only
        // valid for Draft reports. If the user lands here directly
        // (deep link / bookmark / browser back) for a Submitted or
        // Archived report, redirect to the Detail screen with
        // `replace` so the Editor never enters an editable state —
        // we deliberately do NOT set `title` / `content` / `version`,
        // which keeps `version === null` and the render stays on
        // "Loading editor…" until the navigate completes and the
        // component unmounts.
        if (row.status !== 'draft') {
          navigate(`/reports/${row.id}`, { replace: true });
          return;
        }
        setTitle(row.title);
        setContent(row.content);
        setVersion(row.version);
        // PHASE 9: also load any photos already attached so the
        // editor's strip shows committed photos alongside the
        // in-flight upload queue.
        try {
          const list = await photosApi.list(getToken, reportId!);
          if (!cancelled) setCommittedPhotos(list ?? []);
        } catch {
          // Non-fatal — a fresh draft may simply have no photos.
        }
      } catch (e) {
        if (cancelled) return;
        const err = e instanceof ApiException ? e : null;
        setError(err?.message ?? 'Could not load the report.');
      } finally {
        if (!cancelled) setBusy(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [reportId, getToken, navigate]);

  // Debounced auto-save. The effect re-fires whenever title / content /
  // reportId / version change; the timeout itself is the debounce. We
  // gate on `dirtyRef` so that the *first* mount and the version bump
  // after a successful save do not trigger an unnecessary PATCH. We also
  // bail when `inflightRef` is set so a concurrent flush (pagehide /
  // visibilitychange) does not race a stale `base_version`.
  useEffect(() => {
    if (!reportId || version === null) return;
    if (!dirtyRef.current) return;
    const t = window.setTimeout(async () => {
      if (inflightRef.current) {
        // A flush is mid-flight on the same report. The next debounce
        // round (after the flush lands and bumps `version`) will pick
        // up the latest keystrokes; skipping here prevents a 409.
        return;
      }
      const sendTitle = titleRef.current;
      const sendContent = contentRef.current;
      const sendVersion = versionRef.current;
      const sendReportId = reportIdRef.current;
      if (!sendReportId || sendVersion === null) return;
      inflightRef.current = true;
      setSaving(true);
      try {
        const updated = await reportsApi.update(getToken, sendReportId, {
          base_version: sendVersion,
          title: sendTitle,
          content: sendContent,
        });
        setVersion(updated.version);
        setConflict(false);
        setError(null);
        // Mark clean only if the latest keystrokes match what we just
        // sent — if the user typed during the in-flight PATCH, leave the
        // dirty flag set so the next debounce round picks it up.
        if (titleRef.current === sendTitle && contentRef.current === sendContent) {
          dirtyRef.current = false;
        }
      } catch (e) {
        const err = e instanceof ApiException ? e : null;
        if (err?.statusCode === 409) {
          setConflict(true);
        } else {
          setError(err?.message ?? 'Auto-save failed.');
        }
      } finally {
        inflightRef.current = false;
        setSaving(false);
      }
    }, SAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [title, content, reportId, version, getToken]);

  // Best-effort flush when the user navigates away or backgrounds the
  // tab within the debounce window. Fire-and-forget — we cannot await
  // here because the page may be torn down before the request resolves.
  // Browsers do not guarantee that requests fired from these events
  // complete before the page unloads, but a fire-and-forget `fetch`
  // is the standard mitigation (synchronous XHR would block the unload).
  // Idempotency: the server's `Idempotency-Key` header protects against
  // the case where both this flush and the pending debounce PATCH race.
  const flushPending = useCallback(() => {
    if (!dirtyRef.current) return;
    if (inflightRef.current) return;
    const sendReportId = reportIdRef.current;
    const sendVersion = versionRef.current;
    if (!sendReportId || sendVersion === null) return;
    inflightRef.current = true;
    void reportsApi
      .update(getToken, sendReportId, {
        base_version: sendVersion,
        title: titleRef.current,
        content: contentRef.current,
      })
      .then(() => {
        // The flush sent exactly what was in the refs at this moment.
        // If the user typed during the in-flight PATCH, dirty stays
        // true and the next autosave round will pick it up (until then
        // the in-memory state is lost — this is the best we can do on
        // a page-unload path).
        dirtyRef.current = false;
      })
      .catch(() => {
        // Swallow — the user is leaving the page and we have no UI
        // surface to display the failure. Next session restore will
        // reload the last server-known version.
      })
      .finally(() => {
        inflightRef.current = false;
      });
  }, [getToken]);

  useEffect(() => {
    const onPageHide = () => flushPending();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushPending();
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [flushPending]);

  // PHASE 5B-1: warn before the user closes / refreshes the tab while
  // there are unsaved edits. Setting `returnValue` is the only signal
  // modern Chromium / Firefox honour for the native confirmation prompt;
  // the browser supplies its own message text.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      // Block unload while either an upload is in flight OR there are
      // unsaved title/content edits. The editor's autosave is best-
      // effort; a tab close mid-upload would leave the backend in a
      // "pending" state forever and the user with no retry affordance.
      if (!dirtyRef.current && !upload.hasPendingOrInFlight) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [upload.hasPendingOrInFlight]);

  // PHASE 9: drain the serverPhoto queue after each render that
  // changed upload.tasks. Because drainTask pushes to the queue
  // synchronously and the queue is drained atomically, this effect
  // is safe for concurrent uploads: if multiple tasks finish in the
  // same microtask, all their server photos are in the queue and all
  // are added to committedPhotos in one setState call.
  useEffect(() => {
    const batch = serverPhotoQueueRef.current.splice(0); // atomically drain
    if (batch.length === 0) return;
    setCommittedPhotos((prev) => {
      // Deduplicate: if the server already returned a photo (e.g.
      // from a page reload + re-enqueue), don't double-add.
      const existing = new Set(prev.map((p) => p.id));
      const incoming = batch.filter((sp) => !existing.has(sp.id));
      if (incoming.length === 0) return prev;
      return [...prev, ...incoming];
    });
  }, [upload.tasks]);

  // PHASE 5B-1: confirm in-app navigation away from a dirty editor.
  // Centralised so the back button, the browser-back button, and any
  // future programmatic navigations share the same UX. Returns `true`
  // when the navigation should proceed.
  const confirmLeaveIfDirty = useCallback((): boolean => {
    if (!dirtyRef.current) return true;
    return window.confirm(
      'You have unsaved changes. Leave this report anyway? Your most recent edits may be lost.',
    );
  }, []);

  // PHASE 9: delete an existing committed photo. Only called for
  // uploaded (committed) photos — pending/uploading tasks are handled
  // by the upload controller's removeTask().
  const handleDeletePhoto = useCallback(
    async (photoId: string) => {
      if (!reportId) return;
      // Guard: prevent concurrent deletes of the same photo.
      if (deleteInFlightRef.current.has(photoId)) return;
      deleteInFlightRef.current.add(photoId);
      setDeletePhotoError(null);
      try {
        await photosApi.deletePhoto(getToken, reportId, photoId);
        setCommittedPhotos((prev) => prev.filter((p) => p.id !== photoId));
      } catch (err) {
        const msg = err instanceof ApiException ? err.message : 'Could not delete the photo.';
        setDeletePhotoError(msg);
      } finally {
        deleteInFlightRef.current.delete(photoId);
      }
    },
    [reportId, getToken],
  );

  // Open the full-resolution viewer for an existing committed photo.
  const handleViewOriginal = useCallback((photo: ReportPhotoJson) => {
    setViewerPhoto(photo);
  }, []);

  // PHASE 7A: client-side submit validation — title and content must
  // be non-empty after trimming. Sets `submitError`, focuses the first
  // invalid field, and aborts the network roundtrip.
  async function handleSubmit() {
    if (!reportId) return;
    const trimmedTitle = title.trim();
    const trimmedContent = content.trim();
    if (trimmedTitle === '') {
      setSubmitError('Please enter a report title before submitting.');
      titleInputRef.current?.focus();
      return;
    }
    if (trimmedContent === '') {
      setSubmitError('Please enter report content before submitting.');
      contentTextareaRef.current?.focus();
      return;
    }
    setSubmitError(null);
    setSaving(true);
    try {
      // Flush any pending edits.
      const updated = await reportsApi.update(getToken, reportId, {
        base_version: version ?? 1,
        title,
        content,
      });
      setVersion(updated.version);
      const submitted = await reportsApi.submit(getToken, reportId);
      setVersion(submitted.version);
      navigate(`/reports/${reportId}`);
    } catch (e) {
      const err = e instanceof ApiException ? e : null;
      setError(err?.message ?? 'Could not submit the report.');
    } finally {
      setSaving(false);
    }
  }

  if (busy) {
    return <div className="empty">Loading editor…</div>;
  }
  // Mobile lifecycle rule (commit 6343a80): the non-draft redirect
  // path keeps `version === null` while navigate() is in flight.
  // Hold the loading state here so the editable form does not briefly
  // flash before unmount. The "new draft" bootstrap (no routeId) sets
  // version as soon as reportsApi.create resolves, so this gate is
  // transparent for the Draft case.
  if (version === null) {
    return <div className="empty">Loading editor…</div>;
  }

  return (
    <div className="stack">
      {/* Back navigation — prominent, easy to find */}
      <div className="editor-back-nav">
        <button
          className="btn btn--ghost editor-back-btn"
          type="button"
          onClick={() => {
            if (confirmLeaveIfDirty()) navigate('/workspace');
          }}
        >
          ← Back to Workspace
        </button>
        <span className="muted editor-save-status">
          {saving ? 'Saving…' : version === null ? 'Not saved' : `Saved (v${version})`}
        </span>
      </div>

      <div className="card editor-card">
        {error ? <div className="error-banner">{error}</div> : null}
        {submitError ? <div className="error-banner">{submitError}</div> : null}
        {conflict ? (
          <div className="info-banner">
            The cloud has a newer version. Refresh the page to compare and merge your changes.
          </div>
        ) : null}

        <div className="field" style={{ marginBottom: 16 }}>
          <input
            id="title"
            ref={titleInputRef}
            className="input editor-title-input"
            value={title}
            onChange={(e) => {
              dirtyRef.current = true;
              setTitle(e.target.value);
              if (submitError && e.target.value.trim() !== '') setSubmitError(null);
            }}
            placeholder="Report title"
            autoFocus
          />
        </div>

        <textarea
          id="content"
          ref={contentTextareaRef}
          className="textarea editor-body-textarea"
          value={content}
          onChange={(e) => {
            dirtyRef.current = true;
            setContent(e.target.value);
            if (submitError && e.target.value.trim() !== '') setSubmitError(null);
          }}
          placeholder="Describe the site, equipment, work done…"
        />

        {/* PHASE 7A: Web Speech privacy disclosure. Shown once per
            browser; persisted in localStorage. The full notice lives
            in the useSpeechRecognition module. */}
        {!unsupported && !voiceAckShown ? (
          <div className="info-banner editor-voice-privacy" role="status" style={{ marginTop: 8 }}>
            <span style={{ flex: 1 }}>{SPEECH_PRIVACY_NOTICE}</span>
            <button
              type="button"
              className="btn btn--ghost"
              style={{ padding: '4px 10px', fontSize: 13 }}
              onClick={() => {
                try {
                  window.localStorage.setItem(VOICE_ACK_STORAGE_KEY, '1');
                } catch {
                  // ignore
                }
                setVoiceAckShown(true);
              }}
            >
              Got it
            </button>
          </div>
        ) : null}

        {/* Bottom toolbar: voice input + live save status */}
        <div className="editor-bottom-toolbar">
          <span className="muted" style={{ fontSize: 13 }}>
            {saving ? 'Saving…' : version === null ? '' : `✓ Saved (v${version})`}
          </span>
          <div className="editor-media-btns">
            {/* Show text message on unsupported browsers so mobile users see the reason */}
            {unsupported ? (
              <span className="editor-voice-unavailable">🎤 Voice: {unsupportedReason}</span>
            ) : (
              <>
                {/* PHASE 7A: small voice-language selector adjacent to
                    the Voice button. Defaults are derived from
                    navigator.language; user choice persists in
                    localStorage. */}
                <select
                  className="editor-voice-lang-select"
                  aria-label="Voice language"
                  value={voiceLanguage}
                  onChange={(e) => {
                    const next = e.target.value as SpeechLanguage;
                    if (next === 'en-US' || next === 'zh-CN') setVoiceLanguage(next);
                  }}
                  disabled={voiceState === 'listening'}
                >
                  {(Object.keys(SPEECH_LANGUAGE_LABEL) as SpeechLanguage[]).map((code) => (
                    <option key={code} value={code}>
                      {SPEECH_LANGUAGE_LABEL[code]}
                    </option>
                  ))}
                </select>
                <button
                  className={`btn editor-voice-btn${voiceState === 'listening' ? ' editor-voice-btn--active' : ''}`}
                  type="button"
                  title={
                    voiceState === 'idle' || voiceState === 'error'
                      ? 'Tap to start voice input'
                      : voiceState === 'listening'
                        ? 'Listening… tap to stop'
                        : 'Processing…'
                  }
                  onClick={() => {
                    if (voiceState === 'idle' || voiceState === 'error') {
                      setVoiceError(null);
                      startVoice();
                    } else {
                      stopVoice();
                    }
                  }}
                >
                  {voiceState === 'listening'
                    ? '⏹ Listening…'
                    : voiceState === 'processing'
                      ? '⏳ Recognizing…'
                      : '🎤 Voice'}
                </button>
              </>
            )}

            {/* Photo — Web upload (spec #48–#52). Hidden file input
                drives the picker; the button below delegates to it.
                Disabled while no report is loaded yet or while the
                controller reports an upload in flight (the latter is
                cosmetic — we don't actually pause uploads — but it
                matches the Submit gate's UX language). */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/heic,image/heif"
              multiple
              hidden
              onChange={(e) => {
                const list = e.target.files;
                if (!list || list.length === 0) return;
                upload.enqueue(list);
                // Reset the input so the same File can be re-picked
                // later if the first attempt failed and was deleted.
                e.target.value = '';
              }}
            />
            <button
              className="btn"
              type="button"
              data-testid="photo-button"
              title={
                !reportId
                  ? 'Loading report…'
                  : 'Add photos to this report (JPEG / PNG / HEIC, up to 15 MiB each)'
              }
              disabled={!reportId || busy}
              onClick={() => fileInputRef.current?.click()}
            >
              📷 Photo
            </button>
          </div>
        </div>
        {voiceError ? (
          <div className="info-banner" style={{ marginTop: 8 }}>
            {voiceError}
          </div>
        ) : null}
      </div>

      {/* PHASE 9: photo strip — committed photos + in-flight upload
          tiles. Lives below the editor card so the title/content
          inputs remain the primary focus. */}
      <div className="card">
        {upload.errorBanner ? <div className="error-banner">{upload.errorBanner}</div> : null}
        {deletePhotoError ? <div className="error-banner">{deletePhotoError}</div> : null}
        <PhotoStrip
          photos={committedPhotos}
          tasks={upload.tasks}
          getToken={getToken}
          onRetry={(id) => upload.retry(id)}
          onDelete={handleDeletePhoto}
          onViewOriginal={handleViewOriginal}
        />
      </div>

      {viewerPhoto && (
        <PhotoViewer photo={viewerPhoto} getToken={getToken} onClose={() => setViewerPhoto(null)} />
      )}

      {/* Submit — isolated primary CTA, no competing actions */}
      <button
        className="btn btn--primary editor-submit-btn"
        type="button"
        disabled={busy || !reportId || upload.hasPendingOrInFlight}
        onClick={handleSubmit}
      >
        Submit Report
      </button>
    </div>
  );
}
